// Geo Section — interactive world map with geotagged bubbles, plus a
// scrollable list of the same data. Two-level drill:
//
//   World view   ─► one bubble per country
//   Country view ─► one bubble per city/location inside the country
//
// Locations whose names aren't in `LOCATION_COORDS` (geoData.ts) are not
// plotted on the map but still appear in the side list under a small
// "Unmapped" group so the operator can see they exist.

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import type {
  HrDimension,
  HrEmployeeStatus,
  HrFilterMap,
  HrStatsBreakdownRow,
} from '../../types/hrStats';
import { usePeopleBreakdown } from '../../hooks/usePeopleBreakdown';
import SectionCard from './primitives/SectionCard';
import HBarRow from './primitives/HBarRow';
import { ACCENTS } from './primitives/peopleInsightsTokens';
import type { DrillContext } from './drillContextTypes';
import WorldMap, { type GeoPoint } from './geo/WorldMap';
import { centroidForCountry, coordsForLocation } from './geo/geoData';

const MAP_HEIGHT_PX = 380;
const LIST_HEIGHT_PX = MAP_HEIGHT_PX; // keep the right list at the same height as the map

export interface GeoSectionProps {
  /** Page-level filters; the geo section adds its own `country` filter on drill. */
  pageFilters: HrFilterMap;
  employeeStatus: HrEmployeeStatus;
  /** Apply a filter to the whole page (e.g. drill into a city). */
  onApplyFilter: (dim: HrDimension, value: string) => void;
  /** Open the right-side drill panel. */
  onOpenDrill: (ctx: DrillContext) => void;
}

const GeoSection: React.FC<GeoSectionProps> = ({
  pageFilters,
  employeeStatus,
  onApplyFilter,
  onOpenDrill,
}) => {
  // Local "selected country" — distinct from a page filter so the user
  // can preview a country's cities without committing to filtering on it.
  const [selectedCountry, setSelectedCountry] = useState<HrStatsBreakdownRow | null>(null);

  // Country breakdown always uses page filters MINUS `country`, otherwise
  // selecting one country would zero out every other bubble on the map.
  const countryFilters = useMemo(() => {
    const { country: _ignored, ...rest } = pageFilters;
    return rest;
  }, [pageFilters]);

  const countriesQuery = usePeopleBreakdown({
    dimension: 'country',
    filters: countryFilters,
    employeeStatus,
  });

  // Location breakdown is fetched only when a country is selected.
  const locationFilters = useMemo(() => {
    if (!selectedCountry) return countryFilters;
    return { ...countryFilters, country: [selectedCountry.key] };
  }, [countryFilters, selectedCountry]);

  const locationsQuery = usePeopleBreakdown({
    dimension: 'location',
    filters: locationFilters,
    employeeStatus,
    enabled: Boolean(selectedCountry),
  });

  // Memoise the `?? []` fallback so downstream useMemo deps stay stable
  // when the query is still loading (otherwise we hand out a fresh `[]`
  // reference on every render and React re-runs every memo below).
  const countryRows = useMemo(
    () => countriesQuery.data?.rows ?? [],
    [countriesQuery.data],
  );
  const locationRows = useMemo(
    () => locationsQuery.data?.rows ?? [],
    [locationsQuery.data],
  );

  // -------------------------------------------------------- map point sources

  /** When in world view, every country bubble is plotted at its centroid. */
  const countryPoints: GeoPoint[] = useMemo(() => {
    return countryRows.flatMap<GeoPoint>((row) => {
      const coord = centroidForCountry(row.key) ?? centroidForCountry(row.label);
      if (!coord) return [];
      return [
        {
          id: row.key,
          label: row.label,
          count: row.count,
          coord,
          highlighted: selectedCountry?.key === row.key,
        },
      ];
    });
  }, [countryRows, selectedCountry]);

  const locationPoints: GeoPoint[] = useMemo(() => {
    return locationRows.flatMap<GeoPoint>((row) => {
      const coord = coordsForLocation(row.label) ?? coordsForLocation(row.key);
      if (!coord) return [];
      return [
        {
          id: row.key,
          label: row.label,
          count: row.count,
          coord,
        },
      ];
    });
  }, [locationRows]);

  const unmappedLocationCount = locationRows.length - locationPoints.length;
  const unmappedCountryCount = countryRows.length - countryPoints.length;

  // The new map zooms / pans on its own once we hand it a country name —
  // see WorldMap's `focusCountry` prop. No more polygon math here.

  // -------------------------------------------------------- which list to show

  const rowsForList: HrStatsBreakdownRow[] = selectedCountry ? locationRows : countryRows;
  const listMax = rowsForList[0]?.count ?? 0;
  const listLoading = selectedCountry ? locationsQuery.isLoading : countriesQuery.isLoading;
  const listError = selectedCountry ? locationsQuery.isError : countriesQuery.isError;

  // -------------------------------------------------------- render

  const indigo = ACCENTS.indigo;

  return (
    <SectionCard
      title="Global Distribution"
      subtitle={
        selectedCountry
          ? `Locations in ${selectedCountry.label}. Click a bubble or row to drill in.`
          : 'Click a country bubble or row to see the cities within it.'
      }
      icon={
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            backgroundColor: indigo.surface,
            color: indigo.on,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PublicIcon fontSize="small" />
        </Box>
      }
      action={
        <Button
          size="small"
          variant="contained"
          startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
          onClick={() =>
            onOpenDrill(
              selectedCountry
                ? { kind: 'country', label: selectedCountry.label, country: selectedCountry }
                : { kind: 'world', label: 'Global Distribution' },
            )
          }
          sx={{
            backgroundColor: indigo.main,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 1.5,
            boxShadow: 'none',
            '&:hover': { backgroundColor: indigo.on, boxShadow: 'none' },
          }}
        >
          View data — {selectedCountry ? selectedCountry.label : 'Global'}
        </Button>
      }
    >
      {/* Breadcrumb */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
        <Button
          size="small"
          onClick={() => setSelectedCountry(null)}
          sx={{
            textTransform: 'none',
            fontWeight: selectedCountry ? 500 : 700,
            color: selectedCountry ? indigo.on : 'text.primary',
            backgroundColor: selectedCountry ? 'transparent' : '#F1F5F9',
            borderRadius: 1,
            px: 1.25,
            py: 0.25,
            minHeight: 0,
            '&:hover': { backgroundColor: '#F1F5F9' },
          }}
        >
          World
        </Button>
        {selectedCountry && (
          <>
            <ChevronRightIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Box
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: 'text.primary',
                backgroundColor: '#F1F5F9',
                borderRadius: 1,
                px: 1.25,
                py: 0.5,
              }}
            >
              {selectedCountry.label}
            </Box>
            <Button
              size="small"
              startIcon={<KeyboardArrowLeftIcon sx={{ fontSize: 16 }} />}
              onClick={() => setSelectedCountry(null)}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                color: 'text.secondary',
                ml: 0.5,
              }}
            >
              Back
            </Button>
          </>
        )}
      </Stack>

      {countriesQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load country breakdown.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.4fr) minmax(280px, 1fr)' },
          gap: 2,
        }}
      >
        {/* MAP */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            backgroundColor: '#F8FAFC',
            height: MAP_HEIGHT_PX,
          }}
        >
          {countriesQuery.isLoading && !selectedCountry ? (
            <Skeleton variant="rectangular" width="100%" height="100%" />
          ) : selectedCountry && locationsQuery.isLoading ? (
            <Skeleton variant="rectangular" width="100%" height="100%" />
          ) : (
            <WorldMap
              points={selectedCountry ? locationPoints : countryPoints}
              bubbleColor={selectedCountry ? ACCENTS.emerald.main : indigo.main}
              focusCountry={selectedCountry?.label ?? selectedCountry?.key ?? null}
              onPointClick={(p) => {
                if (selectedCountry) {
                  onApplyFilter('location', p.id);
                } else {
                  const row = countryRows.find((r) => r.key === p.id);
                  if (row) setSelectedCountry(row);
                }
              }}
              ariaLabel={
                selectedCountry
                  ? `Headcount by city in ${selectedCountry.label}`
                  : 'Global headcount by country'
              }
            />
          )}
          {/* Hint about unmapped points */}
          {((!selectedCountry && unmappedCountryCount > 0) ||
            (selectedCountry && unmappedLocationCount > 0)) && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: 'rgba(15, 23, 42, 0.7)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.7rem',
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 12 }} />
              {selectedCountry
                ? `${unmappedLocationCount} location${
                    unmappedLocationCount === 1 ? '' : 's'
                  } unmapped — see list`
                : `${unmappedCountryCount} countr${
                    unmappedCountryCount === 1 ? 'y' : 'ies'
                  } unmapped — see list`}
            </Box>
          )}
        </Box>

        {/* SCROLLABLE LIST */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            height: { xs: 'auto', md: LIST_HEIGHT_PX },
            minHeight: 220,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              px: 1.5,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: '#F8FAFC',
            }}
          >
            {selectedCountry
              ? `Locations in ${selectedCountry.label}`
              : 'Countries'}{' '}
            ({rowsForList.length})
          </Typography>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 0.75,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
            }}
          >
            {listLoading ? (
              Array.from({ length: 6 }).map((_, idx) => (
                <Skeleton key={idx} variant="rounded" height={44} />
              ))
            ) : listError ? (
              <Alert severity="error" sx={{ m: 1 }}>
                Failed to load list.
              </Alert>
            ) : rowsForList.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  py: 4,
                }}
              >
                {selectedCountry
                  ? 'No locations recorded for this country.'
                  : 'No country data available.'}
              </Typography>
            ) : (
              rowsForList.map((row) => {
                const drillable = row.key !== '__null__' && row.label !== '(Unspecified)';
                const accent = selectedCountry ? ACCENTS.emerald : ACCENTS.indigo;
                const hasCoord = selectedCountry
                  ? coordsForLocation(row.label) != null || coordsForLocation(row.key) != null
                  : centroidForCountry(row.key) != null ||
                    centroidForCountry(row.label) != null;
                return (
                  <Box key={row.key} sx={{ borderRadius: 1.5 }}>
                    <HBarRow
                      label={
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{ minWidth: 0 }}
                        >
                          <Tooltip
                            title={
                              hasCoord
                                ? 'On map'
                                : 'No coordinates known — not shown on map'
                            }
                            placement="top"
                          >
                            <LocationOnIcon
                              sx={{
                                fontSize: 14,
                                color: hasCoord ? accent.main : 'text.disabled',
                              }}
                            />
                          </Tooltip>
                          <Box
                            component="span"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.label}
                          </Box>
                        </Stack>
                      }
                      count={row.count}
                      maxCount={listMax}
                      color={accent.main}
                      onClick={
                        drillable
                          ? () => {
                              if (selectedCountry) {
                                onApplyFilter('location', row.key);
                              } else {
                                setSelectedCountry(row);
                              }
                            }
                          : undefined
                      }
                      trailing={
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {row.percent.toFixed(1)}%
                          </Typography>
                          {!selectedCountry && drillable && (
                            <ChevronRightIcon
                              sx={{ fontSize: 16, color: 'text.disabled' }}
                            />
                          )}
                        </Stack>
                      }
                    />
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{
          mt: 2,
          display: 'block',
          color: 'text.secondary',
          backgroundColor: alpha(indigo.main, 0.04),
          borderRadius: 1,
          px: 1.25,
          py: 0.75,
        }}
      >
        Showing {employeeStatus === 'BOTH' ? 'all' : employeeStatus.toLowerCase()}{' '}
        employees. Selecting a location adds it as a filter and updates every section
        below.
      </Typography>
    </SectionCard>
  );
};

export default GeoSection;
